import { useState, useLayoutEffect, useCallback, useRef } from 'react';
import { useDroppable } from '@dnd-kit/core';
import type { CardInstance } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import { cardDimensions } from '../../lib/playtester-engine.js';
import HandCard from './HandCard.js';

/**
 * Compute the per-card right-margin (px) for a fanned hand.
 *
 * Returns `normalGap` (positive) when the natural layout fits within `containerW`.
 * Returns a negative value (overlap) whenever the hand would overflow `containerW`
 * at any card count — the card-count threshold was wrong; the only correct trigger
 * is "natural width > available width".
 *
 * Exported so it can be unit-tested independently of DOM.
 */
export function computeHandMargin(
  cardCount: number,
  cardW: number,
  containerW: number,
  normalGap = 6,
): number {
  if (cardCount <= 1 || containerW <= 0) return normalGap;
  // Natural total width if every adjacent pair is spaced by normalGap
  const naturalW = cardCount * cardW + (cardCount - 1) * normalGap;
  // Everything fits — use the preferred positive gap (upper bound)
  if (naturalW <= containerW) return normalGap;
  // Doesn't fit — solve for the margin that makes it fit exactly:
  // n*cardW + (n-1)*margin = containerW  →  margin = (containerW - n*cardW) / (n-1)
  const margin = (containerW - cardCount * cardW) / (cardCount - 1);
  // Never collapse more than 90% — at least 10% of each card remains visible
  return Math.max(margin, -(cardW * 0.9));
}

/**
 * Content-box width of an element, excluding its horizontal padding.
 *
 * All measurement sites must agree on the box: ResizeObserver reports
 * `contentRect` (content box), so a `getBoundingClientRect()` elsewhere would be
 * wider by the horizontal padding and the fan would be computed against space
 * that does not exist.
 */
function measureContentWidth(el: HTMLElement): number {
  const cs = getComputedStyle(el);
  return el.clientWidth - parseFloat(cs.paddingLeft || '0') - parseFloat(cs.paddingRight || '0');
}

export default function HandDisplay() {
  const hand = usePlaytesterStore((s) => s.hand);
  const zoom = usePlaytesterStore((s) => s.zoom);
  const setHoveredCard = usePlaytesterStore((s) => s.setHoveredCard);
  const [hoveredInstance, setHoveredInstance] = useState<CardInstance | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: 'hand' });

  // Measure container width synchronously on mount, then watch for changes
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(measureContentWidth(el));
    const ro = new ResizeObserver((entries) => {
      setContainerWidth(entries[0]?.contentRect.width ?? 0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Re-measure when zoom changes (card size changes → available space changes)
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (el) setContainerWidth(measureContentWidth(el));
  }, [zoom]);

  // Use cardDimensions (the canonical source of truth) so the margin maths
  // always agree with the CSS variables set by PlaytesterView.
  const { cardW } = cardDimensions(zoom);
  const marginRight = computeHandMargin(hand.length, cardW, containerWidth);

  const handleHover = useCallback(
    (instance: CardInstance | null) => {
      setHoveredInstance(instance);
      setHoveredCard(instance?.card ?? null);
    },
    [setHoveredCard],
  );

  function setRef(el: HTMLDivElement | null) {
    containerRef.current = el;
    setDropRef(el);
  }

  return (
    <div
      ref={setRef}
      // pt-6 (24 px) gives the hover-pop (translateY(-12px) scale(1.06)) enough
      // room at the top so it is never clipped — verified mathematically for all
      // zoom levels up to 200% (cardH ≤ 360 px → upward extension ≤ 22.8 px < 24 px).
      // overflow-hidden keeps the hand column from painting over the zone piles.
      // Horizontal padding must exceed the hover-pop's sideways growth, or the
      // last card is clipped on hover: scale(1.06) grows a card by 3% of its
      // width per side = CARD_BASE_W * (ZOOM_MAX/100) * 0.03 = 7.7px at 200%.
      // px-3 (12px) leaves real headroom; px-2 (8px) left only 0.32px, which
      // would break silently if CARD_BASE_W or ZOOM_MAX were ever raised.
      // The old min-h-[calc(var(--pt-card-h)+28px)] is removed: it inflated the row
      // beyond the card height with no benefit; natural sizing (pt-6 + card height)
      // is sufficient.
      className={`flex items-end px-3 pt-6 pb-1.5 overflow-hidden transition ${
        isOver ? 'bg-green-950/20' : ''
      }`}
    >
      {hand.length === 0 ? (
        <span className="text-[10px] text-gray-700 italic self-center">Empty hand</span>
      ) : (
        hand.map((instance, idx) => (
          <HandCard
            key={instance.instanceId}
            instance={instance}
            isHovered={hoveredInstance?.instanceId === instance.instanceId}
            marginRight={idx < hand.length - 1 ? marginRight : 0}
            onHover={handleHover}
          />
        ))
      )}
    </div>
  );
}
